import { fmtDate } from '../../lib/format'
import { Icon } from '../primitives/Icon'
import type { IconName } from '../primitives/Icon'

interface TeamStatCardsProps {
  /** Whole-scope headcount (department for HOD+, team for manager). */
  peopleCount: number
  /** Role-aware label: "People in the department" vs "People in the team". */
  peopleLabel: string
  /** How many people have approved leave overlapping the current week. */
  onLeaveThisWeek: number
  /** Whether the on-leave-this-week filter is currently applied to the list. */
  weekFilterActive: boolean
  /** Toggle the on-leave-this-week filter. */
  onToggleWeekFilter: () => void
  nextHoliday: { name: string; date: string } | null
}

/** A bg / fg / line accent triple, mapped to the app's semantic tokens so the
 *  tiles stay theme-aware (light + dark) rather than hard-coded hexes. */
interface Accent {
  bg: string
  fg: string
  line: string
}

const ACCENT_INFO: Accent = {
  bg: 'var(--info-bg)',
  fg: 'var(--info-fg)',
  line: 'var(--info-line)',
}
const ACCENT_WARNING: Accent = {
  bg: 'var(--warning-bg)',
  fg: 'var(--warning-fg)',
  line: 'var(--warning-line)',
}
const ACCENT_SUCCESS: Accent = {
  bg: 'var(--success-bg)',
  fg: 'var(--success-fg)',
  line: 'var(--success-line)',
}

function tileStyle(accent: Accent): React.CSSProperties {
  return {
    flex: '1 1 180px',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    padding: '14px 16px',
    border: `1px solid ${accent.line}`,
    borderRadius: 12,
    background: accent.bg,
    textAlign: 'left',
    boxSizing: 'border-box',
  }
}

function TileHead({
  icon,
  label,
  accent,
}: {
  icon: IconName
  label: string
  accent: Accent
}): JSX.Element {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <Icon name={icon} size={15} color={accent.fg} />
      <span style={{ fontSize: 12, fontWeight: 600, color: accent.fg }}>
        {label}
      </span>
    </span>
  )
}

/**
 * The three at-a-glance stat tiles above the team roster (folded in from the
 * retired Department → Overview page): headcount for the viewer's scope, how
 * many people are on leave this week (clickable — filters the list), and the
 * next public holiday. Each tile carries a distinct semantic accent (info /
 * warning / success) so they read at a glance and stay theme-aware.
 */
export function TeamStatCards({
  peopleCount,
  peopleLabel,
  onLeaveThisWeek,
  weekFilterActive,
  onToggleWeekFilter,
  nextHoliday,
}: TeamStatCardsProps): JSX.Element {
  const holidayValue = nextHoliday
    ? `${nextHoliday.name} · ${fmtDate(nextHoliday.date)}`
    : 'None scheduled'

  return (
    <div
      data-testid="team-stat-cards"
      style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}
    >
      <div style={tileStyle(ACCENT_INFO)}>
        <TileHead icon="groups" label={peopleLabel} accent={ACCENT_INFO} />
        <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
          {peopleCount}
        </span>
      </div>

      <button
        type="button"
        onClick={onToggleWeekFilter}
        aria-pressed={weekFilterActive}
        title={
          weekFilterActive
            ? 'Showing only people on leave this week — click to clear'
            : 'Show only people on leave this week'
        }
        style={{
          ...tileStyle(ACCENT_WARNING),
          cursor: 'pointer',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          // Active state reads as "selected": a ring + heavier border rather
          // than a colour swap, so the accent stays consistent.
          borderColor: weekFilterActive ? 'var(--warning-fg)' : ACCENT_WARNING.line,
          boxShadow: weekFilterActive
            ? '0 0 0 1px var(--warning-fg) inset'
            : 'none',
        }}
      >
        <TileHead
          icon="flight_takeoff"
          label="On leave this week"
          accent={ACCENT_WARNING}
        />
        <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text)' }}>
          {onLeaveThisWeek}
        </span>
        <span style={{ fontSize: 11, color: 'var(--warning-fg)', opacity: 0.85 }}>
          {weekFilterActive ? 'Filtering the list · click to clear' : 'Click to filter the list'}
        </span>
      </button>

      <div style={tileStyle(ACCENT_SUCCESS)}>
        <TileHead icon="event" label="Next holiday" accent={ACCENT_SUCCESS} />
        <span
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: 'var(--text)',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {holidayValue}
        </span>
      </div>
    </div>
  )
}
